import * as AuthReducer from "./reducer";
import { AppDispatch } from "../store";
import { createUserWithEmailAndPassword, deleteUser, EmailAuthProvider, getAuth, onAuthStateChanged, reauthenticateWithCredential, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, signOut, updatePassword, type User as FirebaseAuthUser, UserCredential } from 'firebase/auth';
import { AuthSignUpParams, ProfileEditParams } from "../../models/auth/auth.params";
import { Collections, CollectionsClass } from "../../utils/firestore/collections";
import { handleErrors } from "../../utils/firestore/handleErrors";
import { User, UserAddress, UserContactInfo, UserSubscription, UserVerifiedContactInfo } from "../../models/auth/auth.model";
import moment from "moment";
import { collection, doc, getDoc, getDocs, query, setDoc, updateDoc, deleteDoc, where } from "firebase/firestore";
import { registerWebFcmToken, unregisterWebFcmToken } from "../../utils/webPush";

class AuthAction {

    static signInAsGuest = (dispatch: AppDispatch) => {
        dispatch(AuthReducer.signInAsGuest());
    }

    /** Firestore rules use request.auth.token.admin; refresh token so claims are visible to Firestore + client. */
    private static mergeCustomClaimsRole = async (
        firebaseUser: FirebaseAuthUser,
        user: User
    ): Promise<User> => {
        const tokenResult = await firebaseUser.getIdTokenResult(true);
        const isAdminClaim = tokenResult.claims.admin === true;
        return {
            ...user,
            role: isAdminClaim ? "admin" : (user.role || "user"),
        };
    };

    private static getUserByEmail = (
        email: string | null,
        firebaseAuthUser: FirebaseAuthUser,
        dispatch: AppDispatch
    ) => {
        const q = query(
            collection(CollectionsClass.instance, Collections.USERS),
            where("email", "==", email)
        );
        getDocs(q)
            .then(async (res) => {
                const data = res.docs[0].data() as User;
                const userWithRole = await AuthAction.mergeCustomClaimsRole(firebaseAuthUser, {
                    ...data,
                    role: data.role || "user",
                });
                dispatch(AuthReducer.signInSuccess(userWithRole));
                void registerWebFcmToken(data.id);
            })
            .catch((e) => dispatch(AuthReducer.setError(handleErrors(e))));
    }

    static signIn = (
        email: string,
        password: string,
        dispatch: AppDispatch
    ) => {
        dispatch(AuthReducer.setIsLoading());
        signInWithEmailAndPassword(getAuth(), email, password)
            .then(res => {
                this.getUserByEmail(res.user.email, res.user, dispatch);
                // if (res.user.emailVerified) {
                //     this.getUserByEmail(res.user.email, res.user, dispatch);
                // } else {
                //     getAuth().signOut();
                //     dispatch(AuthReducer.setError("Ju ende nuk keni verifikuar email-in tuaj. Shikoni email-in tuaj për të parë linkun e verifikimit."));
                // }
            })
            .catch(e => dispatch(AuthReducer.setError(handleErrors(e))));
    }

    private static saveUserToDB = (
        user_credential: UserCredential,
        data: AuthSignUpParams,
        dispatch: AppDispatch
    ) => {
        const user: User = {
            id: user_credential.user.uid,
            first_name: data.first_name,
            last_name: data.last_name,
            date_of_birth: data.date_of_birth,
            addresses: [],
            email: data.email,
            contact: data.contacts,
            verifiedContacts: data.verifiedContacts!,
            status: "pending",
            subscription: null,
            created_at: moment().valueOf(),
            updated_at: null,
            role: "user",
            verified: false,
        };
        const docRef = doc(CollectionsClass.instance, Collections.USERS, `user_${user_credential.user.uid}`);
        setDoc(docRef, user)
            .then(() => {
                dispatch(AuthReducer.signUpSuccess());
                sendEmailVerification(user_credential.user)
            })
            .catch(e => dispatch(AuthReducer.setError(handleErrors(e))));
    }

    static signUp = (
        data: AuthSignUpParams,
        dispatch: AppDispatch
    ) => {
        dispatch(AuthReducer.setIsLoading());
        createUserWithEmailAndPassword(getAuth(), data.email, data.password)
            .then(res => this.saveUserToDB(res, data, dispatch))
            .catch(e => dispatch(AuthReducer.setError(handleErrors(e))));
    }

    static resetPasswordGetCode = (
        email: string,
        dispatch: AppDispatch
    ) => {
        dispatch(AuthReducer.setIsLoading());
        sendPasswordResetEmail(getAuth(), email)
            .then(() => dispatch(AuthReducer.resetPasswordCodeSent()))
            .catch(e => dispatch(AuthReducer.setError(handleErrors(e))));
    }

    // static listenForUserStatusUpdate = (
    //     userId: string | undefined,
    //     dispatch: AppDispatch
    // ) => {
    //     const unsubscribe = CollectionsClass
    //         .usersCollection
    //         .doc(`user_${userId}`)
    //         .onSnapshot(res => {
    //             const user = res.data() as User;
    //             console.log("LISTEN USER:", user)
    //             if (user && user?.status !== "pending") dispatch(AuthReducer.statusChanged(user));
    //         });
    //     return unsubscribe;
    // }

    static getProfile = (
        userId: string,
        dispatch: AppDispatch
    ) => {
        const firebaseUser = getAuth().currentUser;
        if (!firebaseUser || firebaseUser.uid !== userId) {
            return;
        }
        const docRef = doc(CollectionsClass.instance, Collections.USERS, `user_${userId}`);
        getDoc(docRef)
            .then(async (res) => {
                const data = res.data() as User | undefined;
                if (!data) {
                    console.log("GET PROFILE ERROR: missing user document");
                    return;
                }
                const userWithRole = await AuthAction.mergeCustomClaimsRole(firebaseUser, {
                    ...data,
                    role: data.role || "user",
                });
                dispatch(AuthReducer.signInSuccess(userWithRole));
                void registerWebFcmToken(userId);
            })
            .catch(e => console.log("GET PROFILE ERROR:", e));
    }

    /** Call once at app init. When Firebase Auth state changes, loads user from Firestore or clears auth. */
    static listenForAuthState = (dispatch: AppDispatch) => {
        return onAuthStateChanged(getAuth(), (firebaseUser) => {
            if (firebaseUser) {
                AuthAction.getProfile(firebaseUser.uid, dispatch);
            } else {
                dispatch(AuthReducer.signOutSuccess());
            }
        });
    }

    static updateUserProfile = (
        user: User,
        body: ProfileEditParams,
        dispatch: AppDispatch
    ) => {
        dispatch(AuthReducer.setIsLoading());
        const docRef = doc(CollectionsClass.instance, Collections.USERS, `user_${user.id}`);
        const updated = { ...user, ...body, updated_at: moment().valueOf() };
        updateDoc(docRef, updated)
            .then(() => dispatch(AuthReducer.updateUser(updated)))
            .catch((e) => dispatch(AuthReducer.setError(handleErrors(e))));
    }

    static updateUserVerification = (
        user: User,
        verified: boolean,
        dispatch: AppDispatch
    ) => {
        dispatch(AuthReducer.setIsLoading());
        const docRef = doc(CollectionsClass.instance, Collections.USERS, `user_${user.id}`);
        updateDoc(docRef, { verified, updated_at: moment().valueOf() })
            .then(() => dispatch(AuthReducer.updateUser({ ...user, verified, updated_at: moment().valueOf() })))
            .catch((e) => dispatch(AuthReducer.setError(handleErrors(e))));
    }


    static updateUserAddress = (
        user: User,
        addresses: UserAddress[],
        dispatch: AppDispatch
    ) => {
        dispatch(AuthReducer.setIsLoading());
        const docRef = doc(CollectionsClass.instance, Collections.USERS, `user_${user.id}`);
        updateDoc(docRef, { addresses })
            .then(() => dispatch(AuthReducer.updateAddress({ ...user, addresses })))
            .catch((e) => dispatch(AuthReducer.setError(handleErrors(e))));
    }

    static updateUserContacts = (
        user: User,
        contact: UserContactInfo,
        verifiedContacts: UserVerifiedContactInfo,
        dispatch: AppDispatch,
        status?: User["status"]
    ) => {
        dispatch(AuthReducer.setIsLoading());
        const docRef = doc(CollectionsClass.instance, Collections.USERS, `user_${user.id}`);
        const updatedAt = moment().valueOf();
        const nextStatus = status ?? user.status;
        const updatedUser = { ...user, contact, verifiedContacts, updated_at: updatedAt, status: nextStatus };
        updateDoc(docRef, { contact, verifiedContacts, updated_at: updatedAt, status: nextStatus })
            .then(() => dispatch(AuthReducer.updateContact(updatedUser)))
            .catch((e) => dispatch(AuthReducer.setError(handleErrors(e))));
    }

    static updatePassword = (
        email: string,
        currentPassword: string,
        newPassword: string,
        dispatch: AppDispatch
    ) => {
        dispatch(AuthReducer.setIsLoading());
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) {
            dispatch(AuthReducer.setError("Nuk jeni i kyçur."));
            return;
        }
        const credential = EmailAuthProvider.credential(email, currentPassword);
        reauthenticateWithCredential(currentUser, credential)
            .then(() => updatePassword(currentUser, newPassword))
            .then(() => dispatch(AuthReducer.updatePassword()))
            .catch((e) => dispatch(AuthReducer.setError(handleErrors(e))));
    }

    static updateUserSubscription = (
        user: User,
        subscription: UserSubscription | null,
        dispatch: AppDispatch
    ) => {
        dispatch(AuthReducer.setIsLoading());
        const docRef = doc(CollectionsClass.instance, Collections.USERS, `user_${user.id}`);
        updateDoc(docRef, { ...user, subscription, updated_at: moment().valueOf() })
            .then(() => dispatch(AuthReducer.updateUser({ ...user, subscription, updated_at: moment().valueOf() })))
            .catch((e) => dispatch(AuthReducer.setError(handleErrors(e))));
    }

    static signOut = (dispatch: AppDispatch) => {
        const uid = getAuth().currentUser?.uid;
        void unregisterWebFcmToken(uid).finally(() => {
            signOut(getAuth())
                .then(() => dispatch(AuthReducer.signOutSuccess()))
                .catch((e) => {
                    dispatch(AuthReducer.signOutSuccess());
                    console.log("ERROR SIGN OUT:", e);
                });
        });
    }

    static deleteAccount = (
        userId: string,
        dispatch: AppDispatch
    ) => {
        dispatch(AuthReducer.setIsLoading());
        const currentUser = getAuth().currentUser;
        if (!currentUser) {
            dispatch(AuthReducer.setError(handleErrors(new Error("No user signed in"))));
            return;
        }
        const docRef = doc(CollectionsClass.instance, Collections.USERS, `user_${userId}`);
        void unregisterWebFcmToken(userId).finally(() => {
            deleteDoc(docRef)
                .then(() => deleteUser(currentUser))
                .then(() => dispatch(AuthReducer.deleteAccountSuccess()))
                .catch((e) => {
                    const message = e?.code === "auth/requires-recent-login"
                        ? "Për të fshirë llogarinë, dilni dhe kyçuni përsëri, pastaj provoni sërish."
                        : handleErrors(e);
                    dispatch(AuthReducer.setError(message));
                });
        });
    }

    static findUserByEmail = async (email: string | null): Promise<boolean> => {
        if (!email?.trim()) return false;
        const q = query(
            collection(CollectionsClass.instance, Collections.USERS),
            where("email", "==", email.trim().toLowerCase())
        );
        const res = await getDocs(q);
        return !res.empty;
    }

    static clearState = (dispatch: AppDispatch) => {
        dispatch(AuthReducer.clearState());
    }
}

export default AuthAction;